import { Box } from '@mui/material';
import { useMemo } from 'react';
import ChartTooltip from '~/components/Charts/ChartTooltip';
import { ChartPalette, LinearScale } from '~/components/Charts/chartStyle';
import HoverSurface from '~/components/Charts/HoverSurface';
import { nearestIndexByX } from '~/components/Charts/nearestPoint';
import { PlotArea } from '~/components/Charts/plotArea';
import { formatDisplayIntensity } from '~/domain/dicom/intensityUnits';
import { ProfileCurve } from './lineProfileCurves';

/** The x nearest the cursor that any curve holds a sample at, or undefined. */
function nearestSampledX(sampledXs: number[][], x: number): number | undefined {
    let best: number | undefined;
    for (const xs of sampledXs) {
        const index = nearestIndexByX(xs, x);
        if (index < 0) {
            continue;
        }
        const candidate = xs[index];
        if (
            best === undefined ||
            Math.abs(candidate - x) < Math.abs(best - x)
        ) {
            best = candidate;
        }
    }
    return best;
}

/** The value each curve holds at exactly `x`, skipping gaps and curves without one. */
function sliceAt(
    curves: ProfileCurve[],
    x: number
): { curve: ProfileCurve; y: number }[] {
    const slice: { curve: ProfileCurve; y: number }[] = [];
    for (const curve of curves) {
        const point = curve.data.find((candidate) => candidate.x === x);
        if (point && point.y !== null) {
            slice.push({ curve, y: point.y });
        }
    }
    return slice;
}

/**
 * The crosshair and the tooltip over the profile plot: a move snaps to the
 * nearest sampled distance and the tooltip lists every curve's value there,
 * in the display unit. A curve with a gap at that distance is left out.
 */
export default function LineProfileHoverLayer({
    curves,
    xScale,
    yScale,
    plot,
    unit,
    palette,
}: {
    curves: ProfileCurve[];
    xScale: LinearScale;
    yScale: LinearScale;
    plot: PlotArea;
    unit: string;
    palette: ChartPalette;
}) {
    const sampledXs = useMemo(
        () => curves.map((curve) => curve.data.map((point) => point.x)),
        [curves]
    );
    return (
        <HoverSurface plot={plot} testId="line-profile-hover-target">
            {(cursor) => {
                const x = cursor
                    ? nearestSampledX(
                          sampledXs,
                          xScale.invert(cursor.x - plot.left)
                      )
                    : undefined;
                const slice = x !== undefined ? sliceAt(curves, x) : [];
                if (x === undefined || slice.length === 0) {
                    return null;
                }
                const anchorX = plot.left + xScale(x);
                return (
                    <>
                        <g
                            data-testid="line-profile-crosshair"
                            pointerEvents="none"
                        >
                            <line
                                x1={anchorX}
                                x2={anchorX}
                                y1={plot.top}
                                y2={plot.top + plot.height}
                                stroke={palette.crosshair}
                                strokeOpacity={0.5}
                                strokeWidth={1}
                            />
                            {slice.map(({ curve, y }) => (
                                <circle
                                    key={curve.id}
                                    cx={anchorX}
                                    cy={plot.top + yScale(y)}
                                    r={3}
                                    fill={curve.color}
                                />
                            ))}
                        </g>
                        <ChartTooltip
                            data-testid="line-profile-tooltip"
                            plot={plot}
                            palette={palette}
                            anchor={{ x: anchorX, y: plot.top }}
                            lean={
                                anchorX - plot.left < plot.width / 2
                                    ? 'right'
                                    : 'left'
                            }
                            style={{
                                padding: '4px 8px',
                                fontSize: 11,
                                fontVariantNumeric: 'tabular-nums',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            <Box sx={{ mb: 0.25, opacity: 0.75 }}>
                                {`${x.toFixed(2)} cm`}
                            </Box>
                            {slice.map(({ curve, y }) => (
                                <Box
                                    key={curve.id}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: '2px',
                                            backgroundColor: curve.color,
                                            flexShrink: 0,
                                        }}
                                    />
                                    {formatDisplayIntensity(y, unit)}
                                </Box>
                            ))}
                        </ChartTooltip>
                    </>
                );
            }}
        </HoverSurface>
    );
}
